<template>
  <div>
    <VRow>
      <VCol cols="12">
        <VResponsive :aspect-ratio="16/10" class="overflow-hidden">
          <div class="card-actions-top d-sm-flex d-md-block d-lg-flex justify-center justify-sm-space-between justify-md-center justify-lg-space-between">
            <VSelect
              v-model="interview"
              class="card-actions-top__btn card-actions-top__btn_select"
              :items="interviews"
              item-text="label"
              return-object
              hide-details
              dense
              background-color="#f5f5f5"
              height="20px"
            />
          </div>
          <template v-if="hasInterviews">
            <CandidateInterview
              :key="candidateInterviewKey"
              :candidate="candidate"
              :interview="selectedInterview"
              :vacancy="vacancy"
              :are-questions-equal="areQuestionsEqual"
              @online-interview-played="$emit('online-interview-played')"
            />
          </template>
        </VResponsive>
      </VCol>
    </VRow>
  </div>
</template>

<script>
import moment from 'moment';
import ApiMixin from '@/mixins/ApiMixin';
import { useI18n } from 'vue-i18n-composable';
import CandidateInterview from './Interview/CandidateInterview.vue';
import { interviewType as interviewTypeEnum, candidateInterviewType } from '../../enums';

export default {
  name: 'CandidateInterviewResolver',
  components: {
    CandidateInterview,
  },
  mixins: [ApiMixin],
  props: {
    vacancy: {
      type: Object,
      required: true,
    },
    candidate: {
      type: Object,
      required: true,
    },
    areQuestionsEqual: {
      type: Boolean,
      required: true,
      default: true,
    },
  },
  data() {
    return {
      candidateInterviewTypes: candidateInterviewType,
      interviewTypeEnum,
      interview: null,
      candidateInterviewKey: null,
    };
  },
  setup() {
    const { locale, t } = useI18n();

    function getLabelName(type, momentValue) {
      if (locale.value.toLowerCase() === 'ru') {
        return `${t(type)} (${momentValue.format('DD.MM.YYYY HH:mm:ss')})`;
      }

      return `${t(type)} (${momentValue.format('MM/DD/YYYY HH:mm:ss')})`;
    }

    return {
      getLabelName,
    };
  },
  computed: {
    interviews() {
      const interviews = [];

      this.candidate.officeInterviews.forEach((officeInterview) => {
        officeInterview.createdAt = moment(officeInterview.createdAt);
        officeInterview.label = this.getLabelName(officeInterview.type, officeInterview.createdAt);
        interviews.push(officeInterview);
      });
      const onlineInterview = this.candidate?.interview;

      if (onlineInterview) {
        onlineInterview.type = candidateInterviewType.ONLINE_INTERVIEW;
        onlineInterview.createdAt = moment(onlineInterview.createdAt);
        onlineInterview.isInProgress = false;
        onlineInterview.label = this.getLabelName(
          candidateInterviewType.ONLINE_INTERVIEW,
          onlineInterview.createdAt,
        );
        interviews.push(onlineInterview);
      } else if (!this.candidate.canCreateOnlineInterview) {
        const inProcessOnlineInterview = {
          type: candidateInterviewType.ONLINE_INTERVIEW,
          createdAt: moment('01/01/1900', 'DD/MM/YYYY'),
          isInProgress: true,
          label: this.$t('online_interview'),
        };
        interviews.push(inProcessOnlineInterview);
      }

      return interviews.sort((previous, next) => (
        (previous.createdAt.unix() > next.createdAt.unix())
          ? -1
          : ((next.createdAt.unix() > previous.createdAt.unix()) ? 1 : 0)
      ));
    },
    selectedInterview() {
      return this.interview;
    },
    hasInterviews() {
      return !!this.candidate.officeInterviews?.length > 0
        || !this.candidate.canCreateOnlineInterview;
    },
    interviewType: {
      get() {
        return this.$store.state.candidatePage.activeInterviewType;
      },
      set(value) {
        this.$store.commit('candidatePage/setActiveInterviewType', value);
        this.$store.commit('candidatePage/setActiveTab', 0);
      },
    },
    officeInterviewPlayerTime: {
      get() {
        return this.$store.state.candidatePage.officeInterviewPlayerTime;
      },
      set(value) {
        this.$store.commit('candidatePage/setOfficeInterviewPlayerTime', value);
      },
    },
  },
  watch: {
    interview(newValue) {
      this.candidateInterviewKey = Date.now();
    },
  },
  created() {
    this.interviewType = this.resolveInitialInterviewType();
    this.setFirstInterview();
  },
  methods: {
    resolveInitialInterviewType() {
      if (this.candidate.interview) {
        return this.interviewTypeEnum.ONLINE_INTERVIEW;
      }
      if (this.candidate.officeInterview) {
        return this.interviewTypeEnum.OFFICE_INTERVIEW;
      }
      return this.candidate.invitationType;
    },
    setFirstInterview() {
      this.interview = this.getFirstInterview();
    },
    getFirstInterview() {
      return this.interviews.length > 0 ? this.interviews[0] : null;
    },
    changeShowOfficeInterview(newId) {
      const showInterview = this.interviews.find(
        interview => interview.id === newId
          && interview.type !== candidateInterviewType.ONLINE_INTERVIEW,
      );

      if (!showInterview) {
        return;
      }

      if (showInterview.id === this.interview.id) {
        return;
      }

      this.interview = showInterview;
    },
  },
};
</script>
<style lang="scss" scoped>
  .interview-switch {
    &::v-deep {
      .success--text {
        color: map-get($light-blue, 'darken-3') !important;
        caret-color: map-get($light-blue, 'darken-3') !important;
      }
    }
  }
  .card-actions-top {
    align-items: center;
    padding: 0.625rem;
    background-color: #f5f5f5;
    border-radius: 0.5rem 0.5rem 0 0;
    border-bottom: 1px solid #E0E0E0;
    &__btn {
      min-width: 8.75rem;
      font-size: 0.75rem;
      color: #757575;
      background-color: #f5f5f5;
      border-radius: 0.25rem;
      border: 1px solid #E0E0E0;
      padding: 0.3125rem 0.625rem;
      margin-right: 1.25rem;
      &_select {
        max-width: 14.875rem;
        padding: 0.25rem 0.25rem 0.25rem 0.625rem;
        margin-right: 0;
        margin-top: 0;
      }
    }
  }
  .interview-switch {
    &::v-deep {
      .success--text {
        color: map-get($light-blue, 'darken-3') !important;
        caret-color: map-get($light-blue, 'darken-3') !important;
      }
    }
  }
  @media (max-width: 959px) {
    .card-actions-top {
      align-items: center;
      padding: 0.625rem 0;
      border-bottom: none;
    }
  }
</style>
<style lang="scss">
.card-actions-top__btn {
  &.theme--light.v-text-field > .v-input__control > .v-input__slot:before {
    content: none;
  }
  &.card-actions-top__btn_select.v-text-field > .v-input__control > .v-input__slot:before,
  &.card-actions-top__btn_select.v-text-field > .v-input__control > .v-input__slot:after {
    content: none;
  }
  &.v-text-field.v-input--dense:not(.v-text-field--enclosed):not(.v-text-field--full-width) .v-input__prepend-inner .v-input__icon > .v-icon,
  &.v-text-field.v-input--dense:not(.v-text-field--enclosed):not(.v-text-field--full-width) .v-input__append-inner .v-input__icon > .v-icon {
    margin-top: 4px;
  }
  &.card-actions-top__btn_select .v-select__selection.v-select__selection--comma {
    max-width: 100%;
    margin-right: 0;
  }
  &.card-actions-top__btn_select.v-text-field .v-input__append-inner {
    padding-left: 0;
  }
}
</style>

<i18n locale="en" lang="yaml">
  interviewOnline: Online interview
  officeInterview: In-person interview
  online_interview: Virtual interview
  video_call: Video call
  office_interview: Office meeting
  uploaded_video: Video file
</i18n>

<i18n locale="ru" lang="yaml">
  interviewOnline: Онлайн интервью
  officeInterview: Очное интервью
  online_interview: Автоматическое интервью
  video_call: Видеозвонок
  office_interview: Встречу в офисе
  uploaded_video: Файл с видео
</i18n>
